import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Table, Modal } from "antd";
import {
  fstAssignmentSchema,
  fstAssignmentOtpSchema,
} from "../../../Validations/fstAssignmentsValidation";
import {
  assignFstUser,
  getFstAssignmentLogs,
  getFstAssignments,
  getFstUsers,
  getUsersByRole,
} from "../../../apis/fstAssignmentsApi";
import { formatPersonLabel } from "../../../utils/displayName";
import { buildNameEmailSearchParams } from "../../../utils/searchParams";

const LOG_ACTION_LABELS = {
  assigned: "Assigned",
  reassigned: "Reassigned",
  unassigned: "Unassigned",
  password_changed: "Password changed",
  fst_user_created: "Viewer created",
  assignment_otp_sent: "Assignment OTP sent",
  fst_user_inactivated: "Viewer inactivated",
};

const DEFAULT_ASSIGNMENT_FILTERS = {
  fstUserId: "",
  status: "",
};

const formatLogAction = (action) =>
  LOG_ACTION_LABELS[action] || (action ? action.replace(/_/g, " ") : "--");

const formatLogDetails = (record) => {
  if (record.message) return record.message;
  const { details } = record;
  if (!details) {
    const fallback = [
      record.performedBy,
      record.assignedUserName,
      record.fstUserName,
    ]
      .filter(Boolean)
      .join(" · ");
    return fallback || "--";
  }
  if (typeof details === "string") return details;
  if (typeof details === "object") {
    const parts = [];
    if (details.assignedRole) parts.push(`Role: ${details.assignedRole}`);
    if (details.gmoId) parts.push(`GMO ID: ${details.gmoId}`);
    if (details.crmId) parts.push(`CRM ID: ${details.crmId}`);
    if (details.crmContactId)
      parts.push(`CRM Contact: ${details.crmContactId}`);
    return parts.length ? parts.join(" · ") : JSON.stringify(details);
  }
  return "--";
};

const FSTAssignments = () => {
  const [fstUsers, setFstUsers] = useState([]);
  const [filterFstUsers, setFilterFstUsers] = useState([]);
  const [roleUsers, setRoleUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingFst, setLoadingFst] = useState(false);
  const [loadingFilterFst, setLoadingFilterFst] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [fstSearch, setFstSearch] = useState("");
  const [fstFilterSearch, setFstFilterSearch] = useState("");
  const [assignMessage, setAssignMessage] = useState("");
  const [assignMessageType, setAssignMessageType] = useState("danger");
  const [otpChallenge, setOtpChallenge] = useState(null);

  const [assignments, setAssignments] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_ASSIGNMENT_FILTERS);

  const [historyTarget, setHistoryTarget] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");
  useEffect(() => {
    loadFstUsers();
    loadFilterFstUsers();
  }, []);

  useEffect(() => {
    loadAssignments(filters);
  }, [filters]);

  const loadFstUserOptions = async (searchQuery, setList, setLoading) => {
    setLoading(true);
    try {
      const res = await getFstUsers(buildNameEmailSearchParams(searchQuery));
      setList(res?.success ? res.result || [] : []);
    } catch (e) {
      console.log(e);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFstUsers = (searchQuery = "") =>
    loadFstUserOptions(searchQuery, setFstUsers, setLoadingFst);

  const loadFilterFstUsers = (searchQuery = "") =>
    loadFstUserOptions(searchQuery, setFilterFstUsers, setLoadingFilterFst);

  const handleResetAssignmentFilters = () => {
    setFstFilterSearch("");
    setFilters({ ...DEFAULT_ASSIGNMENT_FILTERS });
    loadFilterFstUsers("");
  };

  const loadRoleUsers = async (role, searchQuery = "") => {
    if (!role) {
      setRoleUsers([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const res = await getUsersByRole(
        role,
        buildNameEmailSearchParams(searchQuery),
      );
      if (res?.success) {
        setRoleUsers(res.result || []);
      } else {
        setRoleUsers([]);
      }
    } catch (e) {
      console.log(e);
      setRoleUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadAssignments = async (params = {}) => {
    setAssignmentLoading(true);
    try {
      const query = {};
      if (params.fstUserId) query.fstUserId = params.fstUserId;
      if (params.status && params.status !== "") query.status = params.status;

      const res = await getFstAssignments(query);
      if (res?.success) {
        setAssignments(res.result || []);
      } else {
        setAssignments([]);
      }
    } catch (e) {
      console.log(e);
      setAssignments([]);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const buildAssignPayload = (values, otp) => {
    const body = {
      fstUserId: Number(values.fstUserId),
      userId: Number(values.userId),
      role: values.role,
    };
    if (otp) body.otp = otp;
    return body;
  };

  const clearOtpFlow = () => {
    setOtpChallenge(null);
    otpFormik.resetForm();
  };

  const handleAssignSuccess = (message) => {
    setAssignMessageType("success");
    setAssignMessage(message || "Viewer assigned successfully.");
    clearOtpFlow();
    formik.resetForm();
    setRoleUsers([]);
    loadAssignments(filters);
  };

  const handleAssignError = (message) => {
    setAssignMessageType("danger");
    setAssignMessage(message || "Failed to assign user.");
  };

  const formik = useFormik({
    initialValues: {
      role: "",
      fstUserId: "",
      userId: "",
    },
    validationSchema: fstAssignmentSchema,
    onSubmit: async (values) => {
      setAssignMessage("");
      setAssigning(true);
      try {
        const res = await assignFstUser(buildAssignPayload(values));
        if (res?.success && res?.result?.requiresOtp) {
          setAssignMessageType("info");
          setAssignMessage(
            res.result.message || "OTP sent to your registered mobile.",
          );
          setOtpChallenge({
            message: res.result.message,
            fstUserId: res.result.fstUserId ?? Number(values.fstUserId),
            userId: res.result.userId ?? Number(values.userId),
            role: res.result.role ?? values.role,
          });
          otpFormik.resetForm();
        } else if (res?.success) {
          handleAssignSuccess(res?.message);
        } else {
          handleAssignError(res?.message);
        }
      } catch (e) {
        handleAssignError("Failed to assign user.");
      } finally {
        setAssigning(false);
      }
    },
  });

  const otpFormik = useFormik({
    initialValues: { otp: "" },
    validationSchema: fstAssignmentOtpSchema,
    onSubmit: async (values) => {
      if (!otpChallenge) return;
      setAssignMessage("");
      setVerifyingOtp(true);
      try {
        const res = await assignFstUser({
          fstUserId: Number(otpChallenge.fstUserId),
          userId: Number(otpChallenge.userId),
          role: otpChallenge.role,
          otp: values.otp,
        });
        if (res?.success && !res?.result?.requiresOtp) {
          handleAssignSuccess(res?.message);
        } else if (res?.success && res?.result?.requiresOtp) {
          setAssignMessageType("info");
          setAssignMessage(
            res.result.message || "OTP sent again. Please enter the new code.",
          );
          setOtpChallenge((prev) => ({
            ...prev,
            message: res.result.message,
          }));
        } else {
          handleAssignError(res?.message);
        }
      } catch (e) {
        handleAssignError("Failed to verify OTP.");
      } finally {
        setVerifyingOtp(false);
      }
    },
  });

  const handleCancelOtp = () => {
    clearOtpFlow();
    setAssignMessage("");
  };

  const handleResendOtp = async () => {
    if (!otpChallenge) return;
    setResendingOtp(true);
    setAssignMessage("");
    try {
      const res = await assignFstUser({
        fstUserId: Number(otpChallenge.fstUserId),
        userId: Number(otpChallenge.userId),
        role: otpChallenge.role,
      });
      if (res?.success && res?.result?.requiresOtp) {
        setAssignMessageType("info");
        setAssignMessage(
          res.result.message || "OTP resent to your registered mobile.",
        );
        setOtpChallenge((prev) => ({
          ...prev,
          message: res.result.message,
        }));
        otpFormik.resetForm();
      } else if (res?.success) {
        handleAssignSuccess(res?.message);
      } else {
        handleAssignError(res?.message || "Failed to resend OTP.");
      }
    } catch (e) {
      handleAssignError("Failed to resend OTP.");
    } finally {
      setResendingOtp(false);
    }
  };

  const handleAssignmentFieldChange = (handler) => (e) => {
    if (otpChallenge) clearOtpFlow();
    handler(e);
  };

  const openHistory = async (record) => {
    const fstUserId =
      record.fstUserId ?? record.fstId ?? (filters.fstUserId || undefined);
    const assignmentId = record.assignmentId ?? record.id ?? undefined;

    setHistoryTarget(record);
    setLogs([]);
    setLogsError("");
    setLogsLoading(true);

    try {
      const res = await getFstAssignmentLogs({
        fstUserId,
        assignmentId,
        limit: 100,
      });
      if (res?.success) {
        setLogs(res.result || []);
      } else {
        setLogsError(res?.message || "Failed to load history.");
      }
    } catch (e) {
      setLogsError("Failed to load history.");
    } finally {
      setLogsLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryTarget(null);
    setLogs([]);
    setLogsError("");
  };

  const assignmentColumns = [
    {
      title: "S.No",
      render: (_text, _record, index) => <span>{index + 1}</span>,
      width: "6%",
    },
    {
      title: "Viewer Name",
      dataIndex: "fstUserName",
      render: (v) => <span>{v || "--"}</span>,
      width: "18%",
    },
    {
      title: "Assigned User",
      dataIndex: "assignedUserName",
      render: (v) => <span>{v || "--"}</span>,
      width: "20%",
    },
    {
      title: "Role",
      dataIndex: "role",
      render: (v) => (
        <span>
          {v === "user" ? "User" : v === "broker" ? "Broker" : v || "--"}
        </span>
      ),
      width: "10%",
    },
    {
      title: "Assigned On",
      dataIndex: "assignedOn",
      render: (v) => {
        const d = v ? new Date(v) : null;
        return d && !Number.isNaN(d.getTime()) ? d.toLocaleString() : "--";
      },
      width: "18%",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => <span>{v || "--"}</span>,
      width: "10%",
    },
    {
      title: "Actions",
      render: (_text, record) => (
        <div className="d-flex flex-wrap gap-1">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => openHistory(record)}
          >
            History
          </button>
        </div>
      ),
      width: "16%",
    },
  ];

  const logColumns = [
    {
      title: "When",
      dataIndex: "createdAt",
      render: (v, r) => {
        const ts = v || r.timestamp || r.assignedOn;
        const d = ts ? new Date(ts) : null;
        return d && !Number.isNaN(d.getTime()) ? d.toLocaleString() : "--";
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (v) => <span>{formatLogAction(v)}</span>,
    },
    {
      title: "Details",
      render: (_text, r) => <span>{formatLogDetails(r)}</span>,
    },
  ];

  return (
    <div className="main-wrapper">
      <div className="page-wrapper">
        <div className="content container-fluid">
          <h4 className="mt-3">Viewer Assignment</h4>
          <div className="card mt-3">
            <div className="card-body">
              <form onSubmit={formik.handleSubmit}>
                <div className="row">
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Viewer Role</label>
                    <select
                      name="role"
                      className="form-control"
                      value={formik.values.role}
                      onChange={(e) => {
                        if (otpChallenge) clearOtpFlow();
                        formik.handleChange(e);
                        formik.setFieldValue("userId", "");
                        setUserSearch("");
                        loadRoleUsers(e.target.value, "");
                      }}
                      disabled={!!otpChallenge}
                      onBlur={formik.handleBlur}
                    >
                      <option value="">Select Role</option>
                      <option value="user">User</option>
                      <option value="broker">Broker</option>
                    </select>
                    {formik.touched.role && formik.errors.role && (
                      <div className="text-danger">{formik.errors.role}</div>
                    )}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      Clayo User to be Assigned
                    </label>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search name or email"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            loadRoleUsers(formik.values.role, userSearch);
                          }
                        }}
                        disabled={!!otpChallenge || !formik.values.role}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm text-nowrap"
                        onClick={() =>
                          loadRoleUsers(formik.values.role, userSearch)
                        }
                        disabled={
                          !!otpChallenge || !formik.values.role || loadingUsers
                        }
                      >
                        Search
                      </button>
                    </div>
                    <select
                      name="userId"
                      className="form-control"
                      value={formik.values.userId}
                      onChange={handleAssignmentFieldChange(
                        formik.handleChange,
                      )}
                      onBlur={formik.handleBlur}
                      disabled={
                        !!otpChallenge || !formik.values.role || loadingUsers
                      }
                    >
                      <option value="">
                        {loadingUsers ? "Loading..." : "Select User"}
                      </option>
                      {roleUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {formatPersonLabel(u)}
                          {u.email ? ` (${u.email})` : ""}
                        </option>
                      ))}
                    </select>
                    {formik.touched.userId && formik.errors.userId && (
                      <div className="text-danger">{formik.errors.userId}</div>
                    )}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Viewer to be Assigned</label>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search name or email"
                        value={fstSearch}
                        onChange={(e) => setFstSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            loadFstUsers(fstSearch);
                          }
                        }}
                        disabled={!!otpChallenge}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm text-nowrap"
                        onClick={() => loadFstUsers(fstSearch)}
                        disabled={!!otpChallenge || loadingFst}
                      >
                        Search
                      </button>
                    </div>
                    <select
                      name="fstUserId"
                      className="form-control"
                      value={formik.values.fstUserId}
                      onChange={handleAssignmentFieldChange(
                        formik.handleChange,
                      )}
                      onBlur={formik.handleBlur}
                      disabled={!!otpChallenge || loadingFst}
                    >
                      <option value="">
                        {loadingFst ? "Loading..." : "Select Viewer"}
                      </option>
                      {fstUsers.map((f) => (
                        <option key={f.id} value={f.id}>
                          {formatPersonLabel(f)}
                          {f.email ? ` (${f.email})` : ""}
                        </option>
                      ))}
                    </select>
                    {formik.touched.fstUserId && formik.errors.fstUserId && (
                      <div className="text-danger">
                        {formik.errors.fstUserId}
                      </div>
                    )}
                  </div>
                </div>

                {assignMessage && (
                  <div className={`alert alert-${assignMessageType}`}>
                    {assignMessage}
                  </div>
                )}

                {!otpChallenge && (
                  <div className="d-flex justify-content-end">
                    <button
                      className="btn btn-primary"
                      type="submit"
                      disabled={assigning}
                    >
                      {assigning ? "Sending OTP..." : "Assign Viewer"}
                    </button>
                  </div>
                )}
              </form>

              {otpChallenge && (
                <div className="mt-4 border-top pt-4">
                  <div className="alert alert-info mb-3">
                    <strong>2FA verification required.</strong>
                    <br />
                    {otpChallenge.message ||
                      "Enter the 5-digit OTP sent to your registered mobile."}
                  </div>
                  <form onSubmit={otpFormik.handleSubmit}>
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Enter OTP</label>
                        <input
                          className="form-control"
                          type="text"
                          inputMode="numeric"
                          maxLength={5}
                          name="otp"
                          placeholder="5-digit OTP"
                          value={otpFormik.values.otp}
                          onChange={otpFormik.handleChange}
                          onBlur={otpFormik.handleBlur}
                          autoComplete="one-time-code"
                        />
                        {otpFormik.touched.otp && otpFormik.errors.otp && (
                          <div className="text-danger">
                            {otpFormik.errors.otp}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCancelOtp}
                        disabled={verifyingOtp || resendingOtp}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={handleResendOtp}
                        disabled={verifyingOtp || resendingOtp}
                      >
                        {resendingOtp ? "Resending..." : "Resend OTP"}
                      </button>
                      <button
                        className="btn btn-success"
                        type="submit"
                        disabled={verifyingOtp || resendingOtp}
                      >
                        {verifyingOtp ? "Verifying..." : "Verify OTP & Assign"}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          <div className="card mt-4">
            <div className="card-body">
              <h5 className="mb-3">Viewer Assignments</h5>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label">Filter by Viewer</label>
                  <div className="d-flex gap-2 mb-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search Viewer name or Email address"
                      value={fstFilterSearch}
                      onChange={(e) => setFstFilterSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          loadFilterFstUsers(fstFilterSearch);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm text-nowrap"
                      onClick={() => loadFilterFstUsers(fstFilterSearch)}
                      disabled={loadingFilterFst}
                    >
                      Search
                    </button>
                  </div>
                  <select
                    className="form-control"
                    value={filters.fstUserId}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, fstUserId: e.target.value }))
                    }
                  >
                    <option value="">All Viewers</option>
                    {filterFstUsers.map((f) => (
                      <option key={f.id} value={f.id}>
                        {formatPersonLabel(f)}
                        {f.email ? ` (${f.email})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 d-flex align-items-end gap-2">
                  <label className="form-label">Status</label>
                  <select
                    className="form-control mt-2"
                    value={filters.status}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, status: e.target.value }))
                    }
                  >
                    <option value="">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    
                  </select>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary me-2"
                    onClick={handleResetAssignmentFilters}
                  >
                    Reset
                  </button>
                  {/* <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => loadAssignments(filters)}
                    disabled={assignmentLoading}
                  >
                   {assignmentLoading ? "Loading..." : "Refresh"}
                  </button> */}
                </div>
              </div>

              {assignmentLoading ? (
                <div>Loading...</div>
              ) : assignments.length > 0 ? (
                <Table
                  columns={assignmentColumns}
                  dataSource={assignments}
                  className="table-striped"
                  rowKey={(r, i) =>
                    r.assignmentId ??
                    r.id ??
                    `${r.fstUserName}-${r.assignedOn}-${i}`
                  }
                />
              ) : (
                <div>No assignments found</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={
          historyTarget
            ? `Assignment History — ${historyTarget.fstUserName || ""} / ${historyTarget.assignedUserName || ""}`
            : "Assignment History"
        }
        open={!!historyTarget}
        footer={null}
        onCancel={closeHistory}
        width={720}
        destroyOnClose
      >
        {logsLoading ? (
          <div>Loading history...</div>
        ) : logsError ? (
          <div className="text-danger">{logsError}</div>
        ) : logs.length > 0 ? (
          <Table
            columns={logColumns}
            dataSource={logs}
            pagination={{ pageSize: 10 }}
            size="small"
            rowKey={(r, i) => r.id ?? `${r.action}-${r.createdAt}-${i}`}
          />
        ) : (
          <div>No audit logs found</div>
        )}
      </Modal>
    </div>
  );
};

export default FSTAssignments;
